import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { tokenStorage } from '@/utils/tokenStorage'
import { ApiResponse, ApiError } from '@/types'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const tokens = tokenStorage.getTokens()
        if (tokens?.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Handle 401 errors (unauthorized)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const tokens = tokenStorage.getTokens()
            if (tokens?.refreshToken) {
              // Try to refresh the token
              const refreshResponse = await this.client.post('/auth/refresh', {
                refreshToken: tokens.refreshToken
              })

              const newTokens = {
                ...tokens,
                accessToken: refreshResponse.data.accessToken,
                expiresIn: refreshResponse.data.expiresIn
              }

              tokenStorage.setTokens(newTokens)

              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            tokenStorage.clearTokens()
            window.location.href = '/auth/login'
            return Promise.reject(refreshError)
          }
        }

        // Transform error response — handle nested error objects (e.g., { success: false, error: { code, message } })
        const responseData = error.response?.data
        const nestedError = responseData?.error
        
        const apiError: ApiError = {
          message: nestedError?.message || responseData?.message || error.message || 'An unexpected error occurred',
          code: nestedError?.code || responseData?.code,
          field: responseData?.field,
          details: nestedError?.details || responseData?.details
        }

        return Promise.reject(apiError)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get(url, config)
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post(url, data, config)
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put(url, data, config)
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch(url, data, config)
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete(url, config)
  }

  // Utility method for handling paginated responses
  async getPaginated<T>(
    url: string, 
    params?: Record<string, any>
  ): Promise<ApiResponse<{ items: T[]; total: number; page: number; limit: number; hasMore: boolean }>> {
    const response = await this.get<ApiResponse<{ items: T[]; total: number; page: number; limit: number; hasMore: boolean }>>(url, { params })
    return response.data
  }

  // Utility method for file uploads
  async uploadFile<T>(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    return this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  }

  // Utility method for downloading files
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }
}

export const apiClient = new ApiClient()